import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Bot, ShieldCheck, Sparkles } from 'lucide-react';
import { useAppSelector } from '../../hooks/redux';
import PageHero from '../../components/PageHero';

export default function HowItWorksPage() {
  const content = useAppSelector((s) => s.geo.content);

  return (
    <>
      <PageHero
        badge={`${content.countryLabel} · Journey`}
        title="How"
        titleAccent="it works"
        subtitle="One continuous path: start → AI-assisted credit rating → fundraising. People stay accountable; AI accelerates every stage."
      />
      <section style={{ padding: '40px 0 24px', background: '#FFFBF8' }}>
        <div className="container-wide">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 14,
            }}
          >
            {content.howItWorks.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                style={{ background: 'white', border: '1px solid #F0C8D8', borderRadius: 18, padding: 22 }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, marginBottom: 12 }}>
                  {i + 1}
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>{step.title}</h2>
                <p style={{ fontSize: 14, color: '#6B3A52', margin: 0, lineHeight: 1.65 }}>{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '48px 0 72px', background: '#FFFBF8' }}>
        <div className="container-wide">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'end', justifyContent: 'space-between', gap: 16, marginBottom: 28 }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
                AI across the journey
              </p>
              <h2 style={{ margin: '8px 0 0', fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em' }}>
                How we use AI at every stage
              </h2>
              <p style={{ margin: '8px 0 0', fontSize: 15, color: '#6B3A52', maxWidth: 580, lineHeight: 1.65 }}>
                AI assists; it does not replace regulated judgement or partner credit decisions. Clear separation keeps your raise trustworthy.
              </p>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFF0F5', border: '1px solid #F0C8D8', borderRadius: 999, padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#C2185B' }}>
              <Bot size={16} /> Assistive AI · Human final gates
            </div>
          </div>

          <div style={{ display: 'grid', gap: 18 }}>
            {content.aiJourney.map((stage, i) => (
              <motion.article
                key={stage.stage}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: 0.04 }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 0,
                  background: 'white',
                  border: '1px solid #F0C8D8',
                  borderRadius: 22,
                  overflow: 'hidden',
                  boxShadow: i % 2 === 0 ? '0 10px 36px rgba(194,24,91,0.08)' : undefined,
                }}
                className="ai-stage-card"
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 0 }} className="ai-stage-inner">
                  <div style={{ padding: '26px 28px', background: i % 2 === 0 ? 'linear-gradient(160deg, #FFF0F5 0%, #fff 70%)' : '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <span style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg,#C2185B,#E91E63)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14 }}>
                        {stage.stage}
                      </span>
                      <div>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B6E7E' }}>
                          Stage {stage.stage}
                        </p>
                        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>{stage.title}</h3>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: 15, color: '#6B3A52', lineHeight: 1.7 }}>{stage.body}</p>
                  </div>
                  <div style={{ padding: '26px 28px', borderTop: '1px solid #F0C8D8', display: 'grid', gap: 16 }} className="ai-stage-side">
                    <div>
                      <p style={{ margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#C2185B' }}>
                        <Sparkles size={14} /> What AI does
                      </p>
                      <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#4A2535', fontSize: 14, lineHeight: 1.7 }}>
                        {stage.aiDoes.map((item) => (
                          <li key={item} style={{ marginBottom: 4 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ background: '#FFFBF8', border: '1px solid #F0C8D8', borderRadius: 14, padding: 14 }}>
                      <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B3A52' }}>
                        <ShieldCheck size={14} /> Guardrail
                      </p>
                      <p style={{ margin: 0, fontSize: 13.5, color: '#6B3A52', lineHeight: 1.6 }}>{stage.aiDoesNot}</p>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div style={{ marginTop: 36, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#2D0A1A,#880E4F)', borderRadius: 20, padding: '22px 26px', color: 'white' }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.7 }}>
                {content.hero.comboHighlight}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 17, fontWeight: 700 }}>Ready to begin rating as part of your raise?</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <Link to="/#start" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 999, background: 'white', color: '#C2185B', fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
                Start now <ArrowRight size={16} />
              </Link>
              <Link to="/credit-rating" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 999, border: '1.5px solid rgba(255,255,255,0.45)', color: 'white', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                Rating standards
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (min-width: 900px) {
          .ai-stage-inner { grid-template-columns: 1.15fr 0.85fr !important; }
          .ai-stage-side { border-top: none !important; border-left: 1px solid #F0C8D8; }
        }
      `}</style>
    </>
  );
}
